import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ButtonControl,
  IconControl,
  SidePanelFormControl,
  TabPaneControl,
  TableControl,
} from 'spark-core-dx/components';
import { useCorePermissions, useGenericManager } from 'spark-core-dx/hooks';
import AIPanel from '../../components/AIChat/AIPanel';
import ConvokeCommandBar from '../../components/convoke/ConvokeCommandBar';
import DialogControl from '../../components/convoke/ConvokeDialogControl';
import ConvokeDialogMessage from '../../components/convoke/ConvokeDialogMessage';
import ResourceStateControl from '../resource/components/ResourceStateControl';
import ClientCommandStatusTag from './ClientCommandStatusTag';
import ClientDatabasePartitionWizard from './ClientDatabasePartitionWizard';
import {
  getFirstMetadata,
  getFirstResultItem,
  getOperationEventId,
  getResultMessage,
  isAsyncOperationAccepted,
  isSuccessfulResult,
} from './clientEntryContracts';
import {
  CLIENT_CONFIGURATION_ITEM_NAMES,
  CLIENT_CONFIGURATION_OPERATIONS,
  DOMAIN_FORM_FIELDS,
  DOMAIN_GENERATION_FORM_FIELDS,
  PARTITION_CONNECTION_FORM_FIELDS,
  PARTITION_REQUEST_FORM_FIELDS,
  THEME_MODE_FORM_FIELDS,
  createBlankThemePairDraft,
  createDomainGenerationPreview,
  createThemeDraftFromPair,
  resolveThemeAIUpdate,
  selectFields,
  themeModeCss,
} from './clientConfigurationContracts';
import {
  CLIENT_COMMAND_OPERATION_PERMISSIONS,
  CLIENT_COMMAND_PRIMARY_ITEM_PERMISSIONS,
} from './clientCommandPermissions';

const DATABASE_PARTITION_ROLLBACK_ENABLED = false;

const runOperation = async (
  manager,
  operationName,
  item,
  fallbackMessage,
  options = {},
) => {
  if (!manager?.RunOperation) throw new Error(fallbackMessage);
  const result = await manager.RunOperation({
    operationName,
    item,
    isItem: true,
    isWarning: true,
    supressAllErrors: false,
    ...options,
  });
  if (!isSuccessfulResult(result)) {
    throw new Error(getResultMessage(result, fallbackMessage));
  }
  return getFirstResultItem(result);
};

const ConfigurationState = ({ loading, error, message }) => {
  if (loading) {
    return (
      <ResourceStateControl
        compact
        loading
        title="Loading client configuration"
        loadingText="Loading the current configuration."
      />
    );
  }
  if (error) {
    return (
      <ResourceStateControl
        compact
        tone="error"
        title="Client configuration unavailable"
        message={error}
      />
    );
  }
  return message ? (
    <ResourceStateControl
      compact
      title="Client configuration status"
      message={message}
      role="status"
    />
  ) : null;
};

const ConfigurationActions = ({ children }) => (
  <div className="command-entry__actions">{children}</div>
);

const formatPartitionValue = (value) => String(value ?? '')
  .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .trim();

const formatPartitionSummary = (value) => {
  const summary = String(value ?? '');
  return /^[A-Za-z][A-Za-z0-9]*$/.test(summary)
    && /[a-z0-9][A-Z]/.test(summary)
    ? formatPartitionValue(summary)
    : summary;
};

const DATABASE_PARTITION_PANEL_GROUPS = [{
  key: 'identity',
  displayName: 'Identity',
  message: 'Partition key and expected database.',
  panelKeys: ['identity'],
  statusPanelKey: 'identity',
}, {
  key: 'request',
  displayName: 'Requested Configuration',
  message: 'Storage and migration choices requested for this client.',
  panelKeys: ['request'],
  statusPanelKey: 'request',
}, {
  key: 'readiness-provisioning',
  displayName: 'Readiness & Provisioning',
  message: 'Prerequisite checks and current provisioning evidence.',
  panelKeys: ['prerequisites', 'evidence'],
  statusPanelKey: 'evidence',
}, {
  key: 'lifecycle-migration',
  displayName: 'Lifecycle & Migration',
  message: 'Migration strategy and the latest lifecycle result.',
  panelKeys: ['migration', 'last-operation', 'activation'],
  statusPanelKey: 'migration',
}];

const DATABASE_PARTITION_CONFIGURATION_STATUS_ROWS = new Set([
  'request:Storage Mode',
  'prerequisites:Routing',
  'migration:Strategy',
]);

const getPartitionConfigurationValue = (row) => {
  if (!DATABASE_PARTITION_CONFIGURATION_STATUS_ROWS.has(
    `${row.SourcePanelKey}:${row.Name}`,
  )) {
    return '';
  }

  const value = formatPartitionValue(row.Status);
  return value && ![
    'None',
    'Not Configured',
    'Unknown',
  ].includes(value)
    ? value
    : '';
};

const consolidatePartitionPanels = (panels = [], lifecycleState = '') => {
  const visiblePanels = panels.filter((panel) => panel.Key !== 'target');
  if (!visiblePanels.some((panel) => panel.Key === 'activation')) {
    const activationComplete = [
      'Enabled',
      'RollbackRequired',
    ].includes(lifecycleState);
    const activationPending = lifecycleState === 'Ready';
    const activationState = activationComplete
      ? 'Completed'
      : activationPending
        ? 'Pending'
        : 'Not ready';
    visiblePanels.push({
      Key: 'activation',
      DisplayName: 'Activation and Cutover Evidence',
      State: activationState,
      Rows: [{
        Name: 'ActivateClientDatabasePartition',
        Status: activationState,
        Summary: activationComplete
          ? 'Actual migration and routing cutover completed; dedicated client-required routing is active.'
          : activationPending
            ? 'The dedicated database is prepared, but actual migration and routing cutover are still pending.'
            : 'Actual migration and routing cutover remain unavailable until database deployment completes.',
      }],
    });
  }
  const panelByKey = new Map(
    visiblePanels.map((panel) => [panel.Key, panel]),
  );
  const groupedKeys = new Set(
    DATABASE_PARTITION_PANEL_GROUPS.flatMap((group) => group.panelKeys),
  );
  const consolidatedPanels = DATABASE_PARTITION_PANEL_GROUPS
    .map((group) => {
      const sourcePanels = group.panelKeys
        .map((panelKey) => panelByKey.get(panelKey))
        .filter(Boolean);
      if (!sourcePanels.length) {
        return null;
      }
      const statusPanel = panelByKey.get(group.statusPanelKey)
        || sourcePanels[0];
      return {
        Key: group.key,
        DisplayName: group.displayName,
        Message: group.message,
        State: statusPanel.State,
        Rows: sourcePanels.flatMap((sourcePanel) => (
          (sourcePanel.Rows || []).map((row) => ({
            ...row,
            Status: sourcePanel.Key === 'request'
              && row.Name === 'Migration Strategy'
              && row.Summary !== 'No saved request'
              ? 'Configured'
              : row.Status,
            SourcePanelKey: sourcePanel.Key,
          }))
        )),
      };
    })
    .filter(Boolean);
  const ungroupedPanels = visiblePanels.filter(
    (panel) => !groupedKeys.has(panel.Key),
  );

  return [...consolidatedPanels, ...ungroupedPanels];
};

const getEventManagerHref = (eventId) => (
  `?aiPanel=event&aiPanelRoute=%2Fevent%2Fmanage%2F&aiPanelId=${encodeURIComponent(eventId)}`
);

const ClientDomainRoutingSummary = ({ workspace }) => {
  const domainCount = Array.isArray(workspace?.Domains)
    ? workspace.Domains.length
    : 0;

  return (
    <section
      className="resource-workspace-panel"
      aria-label="Client domain routing summary"
    >
      <div className="resource-workspace-section-title resource-workspace-section-title--embedded">
        <h2>Domain routing summary</h2>
        <p>
          Each domain connects a registered application address to this client.
          When an address is shared by multiple clients, the sign-in screen
          asks the user which client to use. The optional Microsoft Entra
          tenant information then selects that client&apos;s sign-in provider.
        </p>
      </div>
      <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
        <div>
          <dt>Routing entries</dt>
          <dd>{domainCount} configured</dd>
        </div>
        <div>
          <dt>Application route</dt>
          <dd>Application + full domain URL</dd>
        </div>
        <div>
          <dt>Sign-in identity</dt>
          <dd>Optional Entra tenant</dd>
        </div>
        <div>
          <dt>Managed base domain</dt>
          <dd>
            {workspace?.PrimaryApplicationDomain
            || 'Not configured'}
          </dd>
        </div>
        <div>
          <dt>Provisioning</dt>
          <dd>
            {String(workspace?.CurrentEnvironment || '').toUpperCase() === 'LOCAL'
              ? workspace?.ProvisioningConfigurationMessage
                || 'LOCAL uses manual Windows hosts-file entries and does not use Azure provisioning.'
              : workspace?.ProvisioningConfigured
              ? 'Azure hosting configured'
              : workspace?.ProvisioningConfigurationMessage
                || 'Configuration required'}
          </dd>
        </div>
        <div>
          <dt>Latest deployment</dt>
          <dd>
            {String(
              workspace?.CurrentEnvironment || '',
            ).toUpperCase() === 'LOCAL'
              ? 'Not applicable in LOCAL'
              : workspace?.LatestDeploymentEventId ? (
                <a href={getEventManagerHref(
                  workspace.LatestDeploymentEventId,
                )}
                >
                  {workspace.LatestDeploymentEventActive
                    ? 'View deployment progress'
                    : 'View deployment event'}
                  {' · '}
                  {workspace.LatestDeploymentEventStatus || 'Queued'}
                </a>
              ) : 'Not started'}
          </dd>
        </div>
      </dl>
      {workspace?.DomainWarnings?.length ? (
        <div
          className="resource-workspace-access-note resource-workspace-access-note--warning"
          role="status"
        >
          <strong>
            {workspace.DomainWarnings.length === 1
              ? 'Shared domain'
              : 'Shared domains'}
          </strong>
          {workspace.DomainWarnings.map((warning) => (
            <div key={warning}>{warning}</div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

const ClientThemeConfigurationSummary = ({ workspace }) => {
  const pairCount = Number.isFinite(workspace?.PairCount)
    ? workspace.PairCount
    : Array.isArray(workspace?.Pairs)
      ? workspace.Pairs.length
      : 0;

  return (
    <section
      className="resource-workspace-panel"
      aria-label="Client theme configuration summary"
    >
      <div className="resource-workspace-section-title resource-workspace-section-title--embedded">
        <h2>Theme configuration summary</h2>
        <p>
          Theme pairs keep the selected application&apos;s Light and Dark
          presentation together. Administrators can design and preview a draft
          without changing the live experience, then activate the complete pair
          when it is ready.
        </p>
      </div>
      <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
        <div>
          <dt>Theme pairs</dt>
          <dd>{pairCount} configured</dd>
        </div>
        <div>
          <dt>Application</dt>
          <dd>{workspace?.ApplicationName || 'Application not available'}</dd>
        </div>
        <div>
          <dt>Modes</dt>
          <dd>Paired Light + Dark</dd>
        </div>
      </dl>
    </section>
  );
};

const DOMAIN_GENERATION_FIELD_PRESENTATION = Object.freeze({
  RegisteredAppId: {
    DisplaySequence: 1,
  },
  Subdomain: {
    DisplaySequence: 2,
  },
  ManagedDomainPreview: {
    DisplayName: 'Managed Convoke Domain',
    Description: 'Read-only URL calculated from the Subdomain, Current Environment, and configured Application Domain. PROD omits the environment suffix.',
    DisplaySequence: 3,
  },
  CustomerDomain: {
    DisplayName: 'Customer Domain',
    Description: 'Optional customer-controlled HTTPS hostname or origin. Leave blank when the client is not bringing a domain.',
    DisplaySequence: 4,
  },
  IncludeDebugDomain: {
    DisplaySequence: 5,
  },
  DebugDomainPreview: {
    DisplayName: 'Debug Domain to Add',
    Description: 'Read-only localhost origin registered for the selected application.',
    DisplaySequence: 6,
  },
  ConfirmProductionProvisioning: {
    DisplaySequence: 7,
  },
});

export const ClientDomainsScreen = ({
  screen,
  clientId,
  currentEnvironment,
}) => {
  const { canExecute } = useCorePermissions();
  const workspaceMgr = useGenericManager(
    CLIENT_CONFIGURATION_ITEM_NAMES.domainsWorkspace,
  );
  const domainMgr = useGenericManager(
    CLIENT_CONFIGURATION_ITEM_NAMES.domain,
  );
  const generationMgr = useGenericManager(
    CLIENT_CONFIGURATION_ITEM_NAMES.domainGenerationRequest,
  );
  const workspaceMgrRef = useRef(workspaceMgr);
  const domainMgrRef = useRef(domainMgr);
  const generationMgrRef = useRef(generationMgr);
  const [workspace, setWorkspace] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [generationMetadata, setGenerationMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [draft, setDraft] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [generationOpen, setGenerationOpen] = useState(false);
  const [generationDraft, setGenerationDraft] = useState(null);
  const [confirmDeployOpen, setConfirmDeployOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [dnsInstructions, setDnsInstructions] = useState(null);
  workspaceMgrRef.current = workspaceMgr;
  domainMgrRef.current = domainMgr;
  generationMgrRef.current = generationMgr;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const [loaded, metadataResult] = await Promise.all([
        runOperation(
          workspaceMgrRef.current,
          CLIENT_CONFIGURATION_OPERATIONS.loadDomains,
          { ClientId: clientId, CurrentEnvironment: currentEnvironment },
          'Client domains are unavailable.',
        ),
        domainMgrRef.current.GetMetadata({ forceReload: true }),
      ]);
      setWorkspace(loaded);
      setMetadata(getFirstMetadata(metadataResult, domainMgrRef.current));
      if (loaded?.CanProvisionDomains) {
        try {
          const generationMetadataResult =
            await generationMgrRef.current.GetMetadata({
              forceReload: true,
            });
          setGenerationMetadata(getFirstMetadata(
            generationMetadataResult,
            generationMgrRef.current,
          ));
        } catch {
          setGenerationMetadata(null);
        }
      } else {
        setGenerationMetadata(null);
      }
    } catch (ex) {
      setWorkspace(null);
      setError(ex?.message || 'Client domains are unavailable.');
    } finally {
      setLoading(false);
    }
  }, [clientId, currentEnvironment]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const formFields = useMemo(
    () => selectFields(metadata, DOMAIN_FORM_FIELDS),
    [metadata],
  );

  const gridMetadata = useMemo(() => {
    if (!metadata) return metadata;
    return {
      ...metadata,
      Fields: (metadata.Fields || []).map((field) => (
        ['OriginTenantId', 'OriginTenantDomain'].includes(
          String(field?.FieldName || ''),
        )
          ? { ...field, ShowOnGrid: false }
          : field
      )),
    };
  }, [metadata]);

  const generationItem = useMemo(
    () => createDomainGenerationPreview(workspace, generationDraft),
    [generationDraft, workspace],
  );

  const generationFormFields = useMemo(() => {
    const fields = selectFields(
      generationMetadata,
      DOMAIN_GENERATION_FORM_FIELDS,
    );
    const dnsProvider = String(
      generationDraft?.DnsProvider ?? 'Manual',
    ).toLowerCase();
    const usesGoDaddy = ['godaddy', '1'].includes(dnsProvider);
    const usesAzureDns = ['azuredns', '2'].includes(dnsProvider);
    return fields.filter((field) => {
      const name = String(
        field?.FieldName ?? field?.fieldName ?? '',
      );
      if (
        name === 'ConfirmProductionProvisioning'
        && String(currentEnvironment).toUpperCase() !== 'PROD'
      ) return false;
      if (
        name === 'DebugDomainPreview'
        && !generationDraft?.IncludeDebugDomain
      ) return false;
      if (
        ['DnsZone'].includes(name)
        && !usesGoDaddy
        && !usesAzureDns
      ) return false;
      if (
        [
          'GoDaddyCredentialStatus',
          'GoDaddyPersonalAccessToken',
        ].includes(name)
        && !usesGoDaddy
      ) return false;
      if (name === 'AzureDnsZoneResourceId' && !usesAzureDns) {
        return false;
      }
      return true;
    }).map((field) => {
      const name = String(
        field?.FieldName ?? field?.fieldName ?? '',
      );
      return {
        ...field,
        TabId:
          field?.TabId
          || field?.tabId
          || 'Generate Domains|1',
        ...(DOMAIN_GENERATION_FIELD_PRESENTATION[name] || {}),
      };
    });
  }, [
    currentEnvironment,
    generationDraft,
    generationMetadata,
  ]);

  const openAdd = () => {
    setDraft({
      ClientId: clientId,
      RegisteredAppId:
        workspace?.Applications?.length === 1
          ? workspace.Applications[0].ApplicationId
          : '',
      Domain: '',
      OriginTenantId: '',
      OriginTenantDomain: '',
      DomainIdentity: '',
      ConcurrencyToken: workspace?.ClientConcurrencyToken,
    });
    setEditOpen(true);
  };

  const openGenerate = () => {
    setGenerationDraft({
      ...(workspace?.GenerationDraft || {}),
      ClientId: clientId,
      RegisteredAppId:
        workspace?.GenerationDraft?.RegisteredAppId
        || workspace?.DefaultApplicationId
        || '',
      ClientConcurrencyToken: workspace?.ClientConcurrencyToken,
    });
    setGenerationOpen(true);
  };

  const saveDomain = async (updated) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await runOperation(
        workspaceMgrRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.saveDomain,
        {
          ...workspace,
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
          ClientConcurrencyToken: workspace.ClientConcurrencyToken,
          DomainDraft: {
            ...updated,
            ClientId: clientId,
            ConcurrencyToken: workspace.ClientConcurrencyToken,
          },
        },
        'The domain was not saved.',
      );
      setWorkspace(saved);
      setDraft(null);
      setEditOpen(false);
      setMessage('The current-environment domain routing entry was saved.');
      return { Success: true, Items: [saved] };
    } catch (ex) {
      setError(ex?.message || 'The domain was not saved.');
      return { Success: false };
    } finally {
      setBusy(false);
    }
  };

  const removeDomain = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await runOperation(
        workspaceMgrRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.removeDomain,
        {
          ...workspace,
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
          ClientConcurrencyToken: workspace.ClientConcurrencyToken,
          SelectedDomainIdentity: pendingRemove.DomainIdentity,
          ConfirmRoutingImpact: true,
        },
        'The domain was not removed.',
      );
      setWorkspace(saved);
      setPendingRemove(null);
      setMessage('The selected domain routing entry was removed.');
      return { Success: true, Items: [saved] };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage: ex?.message || 'The domain was not removed.',
      };
    } finally {
      setBusy(false);
    }
  };

  const queueDomainOperation = async (
    operationName,
    item,
    fallbackMessage,
    options = {},
  ) => {
    if (!workspaceMgrRef.current?.RunOperation) {
      throw new Error(fallbackMessage);
    }
    const result = await workspaceMgrRef.current.RunOperation({
      operationName,
      item,
      isItem: true,
      isWarning: true,
      supressAllErrors: false,
      ...options,
    });
    if (!isSuccessfulResult(result)) {
      throw new Error(getResultMessage(result, fallbackMessage));
    }
    return result;
  };

  const generateDomains = async (updated) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await runOperation(
        workspaceMgrRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.generateDomains,
        {
          ...workspace,
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
          ClientConcurrencyToken: workspace.ClientConcurrencyToken,
          GenerationDraft: {
            ...updated,
            ClientId: clientId,
            ClientConcurrencyToken: workspace.ClientConcurrencyToken,
            UseCustomerDomain:
              !!String(updated?.CustomerDomain || '').trim(),
            ManagedDomainPreview: '',
            CustomerDomainPreview: '',
            DebugDomainPreview: '',
          },
        },
        'The desired domain records were not saved.',
        { processorType: 1 },
      );
      setWorkspace(saved);
      setGenerationOpen(false);
      setGenerationDraft(null);
      if (String(currentEnvironment || '').toUpperCase() === 'LOCAL') {
        const localInstructions = saved?.Domains?.find((domain) =>
          domain?.DnsInstructions?.some((instruction) =>
            instruction?.RecordType === 'HOSTS'));
        setDnsInstructions(localInstructions || null);
        setMessage(
          'The LOCAL routes were saved without calling Azure. Add the displayed entries to the Windows hosts file manually; Generate Domains does not edit that file.',
        );
      } else {
        setMessage(
          'The desired domain records were saved without calling Azure. Run Deploy Domains to idempotently create or reconcile the hosted resources.',
        );
      }
      return { Success: true, Items: [saved] };
    } catch (ex) {
      setError(ex?.message || 'The desired domain records were not saved.');
      return { Success: false };
    } finally {
      setGenerationDraft((current) => (
        current
          ? { ...current, GoDaddyPersonalAccessToken: '' }
          : current
      ));
      setBusy(false);
    }
  };

  const deployDomains = async (confirmProduction = false) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await queueDomainOperation(
        CLIENT_CONFIGURATION_OPERATIONS.deployDomains,
        {
          ...workspace,
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
          ClientConcurrencyToken: workspace.ClientConcurrencyToken,
          ConfirmProductionDeployment: confirmProduction,
        },
        'Domains were not queued for deployment.',
        {
          processorType: 2,
          queryParams: {
            clientid: clientId,
            environment: currentEnvironment,
          },
        },
      );
      const eventId = getOperationEventId(result);
      if (eventId) {
        setWorkspace((current) => ({
          ...current,
          LatestDeploymentEventId: eventId,
          LatestDeploymentEventStatus: 'Queued',
          LatestDeploymentEventActive: true,
          LatestDeploymentEventUpdatedUtc:
            new Date().toISOString(),
        }));
      }
      setConfirmDeployOpen(false);
      setMessage(
        eventId
          ? 'Deploy Domains was queued as an async event. Use the deployment link in the summary to follow its Status Log.'
          : 'Deploy Domains was queued. Refresh the workspace to load its Event Manager link and status.',
      );
      return { Success: true, Items: [result] };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage:
          ex?.message || 'Domains were not queued for deployment.',
      };
    } finally {
      setBusy(false);
    }
  };

  const reconcileDomain = async (domain) => {
    if (!domain?.RetryEligible) {
      setError('The selected domain is not eligible for provisioning retry.');
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await queueDomainOperation(
        CLIENT_CONFIGURATION_OPERATIONS.reconcileDomainProvisioning,
        {
          ...workspace,
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
          ClientConcurrencyToken: workspace.ClientConcurrencyToken,
          SelectedDomainIdentity: domain.DomainIdentity,
        },
        'Domain provisioning was not queued for reconciliation.',
        {
          processorType: 2,
          queryParams: {
            clientid: clientId,
            environment: currentEnvironment,
          },
        },
      );
      setMessage(
        'Domain provisioning reconciliation was queued. Refresh to review the persisted readiness state.',
      );
    } catch (ex) {
      setError(
        ex?.message
        || 'Domain provisioning was not queued for reconciliation.',
      );
    } finally {
      setBusy(false);
    }
  };

  const canSaveDomain = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientDomain,
  );
  const canRemoveDomain = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.removeClientDomain,
  );
  const canGenerateDomains = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.generateClientDomains,
  );
  const canDeployDomains = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.deployClientDomains,
  );
  const canReconcileDomains = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.reconcileClientDomainProvisioning,
  );
  const isLocalEnvironment =
    String(currentEnvironment || '').toUpperCase() === 'LOCAL';
  const hasGeneratedHostedDomains = (workspace?.Domains || []).some(
    (domain) => (
      domain?.DomainKind === 'Managed'
      || domain?.DomainKind === 'Customer'
    ),
  );
  const additionalRowActions = () => ([
    ...(workspace?.CanProvisionDomains && canReconcileDomains ? [
      {
        name: 'Retry Provisioning',
        icon: 'refresh',
        customFunction: reconcileDomain,
      },
    ] : []),
    ...(workspace?.CanProvisionDomains ? [{
        name: 'View Setup Instructions',
        icon: 'view',
        customFunction: (domain) => setDnsInstructions(domain),
      }] : []),
    ...(canRemoveDomain ? [{
      name: 'Remove',
      icon: 'delete',
      customFunction: setPendingRemove,
    }] : []),
  ]);
  const commandBarActions = [
    {
      key: 'refresh-client-domains',
      label: 'Refresh',
      iconKey: 'refresh',
      disabled: loading || busy,
      onClick: refresh,
    },
    ...(canSaveDomain ? [{
      key: 'add-client-domain',
      label: 'Add Domain',
      iconKey: 'add',
      disabled: !workspace || !metadata || busy,
      onClick: openAdd,
    }] : []),
    ...(workspace?.CanProvisionDomains && canGenerateDomains ? [{
      key: 'generate-client-domains',
      label: 'Generate Domains',
      iconKey: 'add',
      disabled: !workspace || !generationMetadata || busy,
      onClick: openGenerate,
    }] : []),
    ...(workspace?.CanProvisionDomains && canDeployDomains && !isLocalEnvironment ? [{
      key: 'deploy-client-domains',
      label: 'Deploy Domains',
      iconKey: 'cloud_upload',
      disabled: !workspace || !hasGeneratedHostedDomains || busy,
      onClick: () => {
        if (
          String(currentEnvironment || '').toUpperCase() === 'PROD'
        ) {
          setConfirmDeployOpen(true);
        } else {
          deployDomains(false);
        }
      },
    }] : []),
  ];

  return (
    <div className="resource-workspace-overview command-entry command-configuration">
      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Domains actions"
        className="convoke-command-bar--screen"
        useSparkButtons
      />
      <ConfigurationState loading={loading} error={error} message={message} />
      {!loading && workspace && metadata ? (
        <>
          <ClientDomainRoutingSummary workspace={workspace} />
          <TableControl
            data={workspace.Domains || []}
            defaultNumRows={50}
            disableTableTitle
            enableMultiSelect={false}
            filters={domainMgr?.filters}
            formFields={formFields}
            headerFeatures={[
              'search',
              'filter',
              'tableStyles',
              'columnSettings',
            ]}
            loadingState={loading || busy}
            metadata={[gridMetadata]}
            mgr={domainMgr}
            onSave={canSaveDomain ? saveDomain : undefined}
            additionalQuickViewList={additionalRowActions}
          />
        </>
      ) : null}

      <SidePanelFormControl
        open={editOpen}
        title="Add Domain"
        metadata={metadata}
        formFields={formFields}
        itemData={draft}
        onSave={saveDomain}
        onCancel={() => setEditOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText={busy ? 'Saving...' : 'Save'}
        setOpenEditSidePanel={setEditOpen}
        setItemData={setDraft}
        viewOnly={false}
      />

      <SidePanelFormControl
        open={generationOpen}
        title="Generate Domains"
        metadata={generationMetadata}
        formFields={generationFormFields}
        itemData={generationItem}
        onSave={generateDomains}
        onCancel={() => setGenerationOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText={busy ? 'Saving...' : 'Generate Domains'}
        setOpenEditSidePanel={setGenerationOpen}
        setItemData={setGenerationDraft}
        viewOnly={false}
      />

      <DialogControl
        openDialog={confirmDeployOpen}
        title="Deploy Production domains"
        subTitle="This starts an asynchronous, idempotent Azure deployment for every generated PROD domain in the selected client."
        okText="Deploy Domains"
        cancelText="Cancel"
        onCancel={() => setConfirmDeployOpen(false)}
        onX={() => setConfirmDeployOpen(false)}
        onOk={() => deployDomains(true)}
        disableOk={busy}
        loadingOk={busy}
      >
        <p>
          Confirm that the saved Production domain set is ready to reconcile
          with Azure DNS and Front Door. Repeating this deployment ensures the
          same resources and does not create duplicates.
        </p>
      </DialogControl>

      <DialogControl
        openDialog={!!pendingRemove}
        title="Remove client domain"
        subTitle={
          String(currentEnvironment || '').toUpperCase() === 'LOCAL'
            ? 'The route is retired in Convoke. Remove its exact entry from the Windows hosts file manually; Generate Domains never edits that file or calls Azure in LOCAL.'
            :
          pendingRemove?.DomainKind === 'Managed'
          || pendingRemove?.DomainKind === 'Customer'
            ? 'The route is retired first. Convoke then removes only the exact Front Door and managed-DNS resources that carry recorded Convoke ownership evidence; customer DNS is never changed.'
            : 'This removes only the selected manual current-environment routing entry.'
        }
        okText="Confirm Remove"
        cancelText="Cancel"
        onCancel={() => setPendingRemove(null)}
        onX={() => setPendingRemove(null)}
        onOk={removeDomain}
        disableOk={busy}
        loadingOk={busy}
      >
        <p>
          Remove {pendingRemove?.Domain || 'the selected domain'} from this
          client after reviewing its routing impact?
        </p>
      </DialogControl>

      <DialogControl
        openDialog={!!dnsInstructions}
        title={
          dnsInstructions?.DnsInstructions?.some((instruction) =>
            instruction?.RecordType === 'HOSTS')
            ? 'LOCAL hosts-file instructions'
            : 'Customer DNS instructions'
        }
        subTitle={
          dnsInstructions?.ProvisioningMessage
          || 'Use the exact Azure Front Door validation records below.'
        }
        okText="Close"
        cancelText=""
        onCancel={() => setDnsInstructions(null)}
        onX={() => setDnsInstructions(null)}
        onOk={() => setDnsInstructions(null)}
      >
        {dnsInstructions?.DnsInstructions?.length ? (
          <dl className="resource-workspace-facts">
            {dnsInstructions.DnsInstructions.map((instruction) => (
              <div
                key={`${instruction.RecordType}-${instruction.Host}`}
              >
                <dt>
                  {instruction.RecordType} · {instruction.Host}
                </dt>
                <dd>{instruction.Value}</dd>
                <dd>{instruction.Purpose}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p>
            No domain setup records are currently required for this route.
          </p>
        )}
      </DialogControl>
    </div>
  );
};

const PartitionPanels = ({ workspace }) => (
  <>
    <div className="resource-workspace-metric-grid" aria-label="Partition metrics">
      {(workspace?.Metrics || [])
        .filter((metric) => (
          DATABASE_PARTITION_ROLLBACK_ENABLED || metric.Key !== 'rollback'
        ))
        .map((metric) => {
          const isMigrationMetric = metric.Key === 'migration';
          const migrationCompleted = isMigrationMetric
            && ['Enabled', 'RollbackRequired']
              .includes(workspace?.LifecycleState);

          return (
            <div className="resource-workspace-metric" key={metric.Key}>
              <span>{isMigrationMetric ? 'Migration' : metric.DisplayName}</span>
              <strong>{formatPartitionValue(
                migrationCompleted ? 'Completed' : metric.Value,
              )}</strong>
            </div>
          );
        })}
    </div>
    <div className="resource-workspace-two-column">
      {consolidatePartitionPanels(
        workspace?.Panels,
        workspace?.LifecycleState,
      )
        .map((panel) => (
          <section className="resource-workspace-panel" key={panel.Key}>
            <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
              <div>
                <h2>{panel.DisplayName}</h2>
                {panel.Message ? <p>{panel.Message}</p> : null}
              </div>
              <ClientCommandStatusTag
                status={formatPartitionValue(panel.State)}
                tone={
                  panel.Key === 'lifecycle-migration'
                  && workspace?.LifecycleState === 'Ready'
                    ? 'warning'
                    : undefined
                }
              />
            </div>
            {panel.Rows?.length ? (
              <div className="resource-workspace-record-list">
                {panel.Rows.map((row) => {
                  const configurationValue = getPartitionConfigurationValue(row);
                  return (
                    <article
                      className="resource-workspace-record-row"
                      key={[
                        panel.Key,
                        row.SourcePanelKey,
                        row.Name,
                        row.Summary,
                      ].filter(Boolean).join('-')}
                    >
                      <div>
                        <strong>
                          {formatPartitionValue(row.Name)}
                          {configurationValue
                            ? ` (${configurationValue})`
                            : ''}
                        </strong>
                        <span>{formatPartitionSummary(row.Summary)}</span>
                      </div>
                      <ClientCommandStatusTag
                        status={configurationValue
                          ? 'Configured'
                          : formatPartitionValue(row.Status)}
                        tone={
                          row.SourcePanelKey === 'activation'
                          && row.Status === 'Pending'
                            ? 'warning'
                          : undefined
                        }
                      />
                    </article>
                  );
                })}
              </div>
            ) : null}
          </section>
        ))}
    </div>
  </>
);

export const ClientDatabasePartitionScreen = ({
  screen,
  clientId,
  appId,
  currentEnvironment,
}) => {
  const { canExecute } = useCorePermissions();
  const manager = useGenericManager(
    CLIENT_CONFIGURATION_ITEM_NAMES.partitionWorkspace,
  );
  const managerRef = useRef(manager);
  const [workspace, setWorkspace] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [wizardError, setWizardError] = useState('');
  const [wizardMessage, setWizardMessage] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [migrationInventory, setMigrationInventory] = useState(null);
  const [connectionDraft, setConnectionDraft] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  managerRef.current = manager;

  const load = useCallback(async (operationName) => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const [loaded, metadataResult] = await Promise.all([
        runOperation(
          managerRef.current,
          operationName,
          {
            ClientId: clientId,
            ApplicationId: appId,
            CurrentEnvironment: currentEnvironment,
          },
          'Client database partition status is unavailable.',
        ),
        managerRef.current.GetMetadata({ forceReload: true }),
      ]);
      setWorkspace(loaded);
      setMetadata(getFirstMetadata(metadataResult, managerRef.current));
    } catch (ex) {
      setWorkspace(null);
      setError(ex?.message || 'Client database partition status is unavailable.');
    } finally {
      setLoading(false);
    }
  }, [appId, clientId, currentEnvironment]);

  useEffect(() => {
    setMigrationInventory(null);
    load(CLIENT_CONFIGURATION_OPERATIONS.loadPartition);
  }, [load]);

  const formFields = useMemo(
    () => selectFields(metadata, PARTITION_REQUEST_FORM_FIELDS),
    [metadata],
  );

  const connectionFormFields = useMemo(
    () => selectFields(metadata, PARTITION_CONNECTION_FORM_FIELDS),
    [metadata],
  );

  const saveRequest = async (updated) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await runOperation(
        managerRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.savePartitionRequest,
        {
          ...workspace,
          ...updated,
          ClientId: clientId,
          ApplicationId: appId,
          CurrentEnvironment: currentEnvironment,
          ConfigConcurrencyToken: workspace.ConfigConcurrencyToken,
          RequestConcurrencyToken: workspace.RequestConcurrencyToken,
        },
        'The partition request was not saved.',
      );
      setWorkspace(saved);
      setMessage('Configuration saved.');
      return { Success: true, Items: [saved] };
    } catch (ex) {
      setError(ex?.message || 'The partition request was not saved.');
      return { Success: false };
    } finally {
      setBusy(false);
    }
  };

  const updateDedicatedConnection = async (updated) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await runOperation(
        managerRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.updatePartitionConnection,
        {
          ...workspace,
          DedicatedConnectionInput:
            updated?.DedicatedConnectionInput || '',
          ClientId: clientId,
          ApplicationId: appId,
          CurrentEnvironment: currentEnvironment,
          ConfigConcurrencyToken: workspace.ConfigConcurrencyToken,
          RequestConcurrencyToken: workspace.RequestConcurrencyToken,
        },
        'The dedicated connection was not updated.',
      );
      setWorkspace(saved);
      setConnectionOpen(false);
      setConnectionDraft(null);
      setMessage('Dedicated connection updated.');
      return { Success: true, Items: [saved] };
    } catch (ex) {
      setError(ex?.message || 'The dedicated connection was not updated.');
      return { Success: false };
    } finally {
      setBusy(false);
    }
  };

  const beginAction = (
    operationName,
    title,
    description,
    requiresReconcile = false,
    asyncOperation = false,
  ) => {
    setPendingAction({
      operationName,
      title,
      description,
      requiresReconcile,
      asyncOperation,
      step: 1,
    });
  };

  const loadDeploymentInventory = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const loaded = await runOperation(
        managerRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.loadPartitionDeploymentInventory,
        {
          ...workspace,
          ClientId: clientId,
          ApplicationId: appId,
          CurrentEnvironment: currentEnvironment,
        },
        'Deployment information is unavailable.',
      );
      setMigrationInventory(
        Array.isArray(loaded?.DeploymentInventory)
          ? loaded.DeploymentInventory
          : [],
      );
      setWorkspace(loaded);
      setInventoryOpen(true);
    } catch (ex) {
      setError(ex?.message || 'Deployment information is unavailable.');
    } finally {
      setBusy(false);
    }
  };

  const runLifecycleAction = async () => {
    if (pendingAction?.requiresReconcile && pendingAction.step === 1) {
      setPendingAction((current) => ({ ...current, step: 2 }));
      return undefined;
    }
    setBusy(true);
    setWizardError('');
    setWizardMessage('');
    try {
      if (pendingAction.asyncOperation) {
        const result = await managerRef.current.RunOperation({
          operationName: pendingAction.operationName,
          item: {
            ...workspace,
            ClientId: clientId,
            ApplicationId: appId,
            CurrentEnvironment: currentEnvironment,
            ConfigConcurrencyToken: workspace.ConfigConcurrencyToken,
            RequestConcurrencyToken: workspace.RequestConcurrencyToken,
            ConfirmLifecycleImpact: true,
          },
          isItem: true,
          isWarning: true,
          supressAllErrors: false,
          processorType: 2,
          queryParams: {
            clientid: clientId,
            applicationid: appId,
            environment: currentEnvironment,
          },
        });
        if (
          !isSuccessfulResult(result)
          && !isAsyncOperationAccepted(result)
        ) {
          throw new Error(getResultMessage(
            result,
            'The client database deployment was not queued.',
          ));
        }
        const eventId = getOperationEventId(result);
        setWorkspace((current) => ({
          ...current,
          DeploymentEventId: eventId || current?.DeploymentEventId,
          DeploymentStatus: 'Queued',
          DeploymentMessage:
            'The client database deployment was queued. Refresh to view its status.',
          LifecycleState: 'Deploying',
          CanDeploy: false,
          WizardSteps: (current?.WizardSteps || []).map((step) => (
            step.Key === 'deploy'
              ? {
                ...step,
                CanExecute: false,
                Status: 'Blocked',
                Message:
                  'Deployment is running. Refresh to view the latest status.',
              }
              : step
          )),
        }));
        setWizardMessage(
          'Database deployment started. Refresh this step to view its latest status.',
        );
        setPendingAction(null);
        return { Success: true };
      }
      const saved = await runOperation(
        managerRef.current,
        pendingAction.operationName,
        {
          ...workspace,
          ClientId: clientId,
          ApplicationId: appId,
          CurrentEnvironment: currentEnvironment,
          ConfigConcurrencyToken: workspace.ConfigConcurrencyToken,
          RequestConcurrencyToken: workspace.RequestConcurrencyToken,
          ConfirmLifecycleImpact: true,
          ConfirmRollbackReconciled:
            pendingAction.requiresReconcile && pendingAction.step === 2,
        },
        'The partition lifecycle operation did not complete.',
      );
      setWorkspace(saved);
      setWizardMessage(
        saved.LastOperationMessage || 'The lifecycle step completed.',
      );
      setPendingAction(null);
      return { Success: true, Items: [saved] };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage:
          ex?.message || 'This database setup step could not be started.',
      };
    } finally {
      setBusy(false);
    }
  };

  const showsDatabaseDestination = [
    CLIENT_CONFIGURATION_OPERATIONS.preparePartition,
    CLIENT_CONFIGURATION_OPERATIONS.validatePartition,
    CLIENT_CONFIGURATION_OPERATIONS.deployPartition,
    CLIENT_CONFIGURATION_OPERATIONS.activatePartition,
  ].includes(pendingAction?.operationName);

  const partitionPermissions = {
    identity: canExecute(
      CLIENT_COMMAND_OPERATION_PERMISSIONS.setUpClientDatabasePartitionIdentity,
    ),
    configuration: canExecute(
      CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientDatabasePartitionRequest,
    ),
    prepare: canExecute(
      CLIENT_COMMAND_OPERATION_PERMISSIONS.prepareClientDatabasePartition,
    ),
    validate: canExecute(
      CLIENT_COMMAND_OPERATION_PERMISSIONS.validateClientDatabasePartition,
    ),
    deploy: canExecute(
      CLIENT_COMMAND_OPERATION_PERMISSIONS.deployClientDatabasePartition,
    ),
    activate: canExecute(
      CLIENT_COMMAND_OPERATION_PERMISSIONS.activateClientDatabasePartition,
    ),
  };
  const canUpdateConnection = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.updateClientDatabasePartitionConnection,
  );
  const canLoadDeploymentInventory = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.loadClientDatabasePartitionDeploymentInventory,
  );
  const identityUnavailable =
    workspace?.CapabilityStatus === 'Unavailable';
  const setupComplete =
    workspace?.LifecycleState === 'Enabled';
  let deploymentInventory = [];
  if (Array.isArray(migrationInventory)) {
    deploymentInventory = [...migrationInventory];
  } else if (Array.isArray(workspace?.DeploymentInventory)) {
    deploymentInventory = [...workspace.DeploymentInventory];
  }
  deploymentInventory.sort((left, right) => {
    const recordDifference = Number(right.SourceRecordCount || 0)
      - Number(left.SourceRecordCount || 0);
    if (recordDifference !== 0) return recordDifference;
    return String(left.CollectionName || '').localeCompare(
      String(right.CollectionName || ''),
      undefined,
      { sensitivity: 'base' },
    );
  });
  const deploymentSourceRecordCount = deploymentInventory.reduce(
    (total, entry) => total + Number(entry.SourceRecordCount || 0),
    0,
  );
  const wizardWorkspace = useMemo(
    () => (
      workspace && Array.isArray(migrationInventory)
        ? {
          ...workspace,
          DeploymentInventory: migrationInventory,
          DeploymentInventoryLoaded: true,
        }
        : workspace && {
          ...workspace,
          DeploymentInventoryLoaded: Boolean(
            workspace.DeploymentInventoryCapturedAtUtc,
          ),
        }
    ),
    [migrationInventory, workspace],
  );
  const commandBarActions = [
    {
      key: 'refresh-database-partition',
      label: 'Refresh',
      iconKey: 'refresh',
      disabled: loading || busy,
      onClick: () => load(
        CLIENT_CONFIGURATION_OPERATIONS.loadPartitionStatus,
      ),
    },
    ...(canUpdateConnection && workspace?.CanUpdateDedicatedConnection ? [{
      key: 'update-database-partition-connection',
      label: 'Update Dedicated Connection',
      iconKey: 'link',
      disabled: !metadata || busy,
      onClick: () => {
        setConnectionDraft({
          ...workspace,
          DedicatedConnectionInput: '',
        });
        setConnectionOpen(true);
      },
    }] : []),
    ...(canLoadDeploymentInventory ? [{
      key: 'database-partition-migration-check',
      label: 'Migration Check',
      iconKey: 'database',
      disabled: loading || busy || !workspace,
      onClick: loadDeploymentInventory,
    }] : []),
    ...(partitionPermissions.activate && setupComplete ? [{
      key: 'reconcile-database-partition-source',
      label: 'Reconcile Shared Data',
      iconKey: 'sync',
      disabled: !workspace?.CanActivate || busy,
      onClick: () => beginAction(
        CLIENT_CONFIGURATION_OPERATIONS.activatePartition,
        'Reconcile remaining shared data',
        'Copy any remaining active shared records to the dedicated database, verify every copy, and soft-delete only the verified shared records. This operation is safe to repeat.',
      ),
    }] : []),
    {
      key: 'manage-database-partition',
      label: 'Manage Database Partition',
      iconKey: 'settings',
      disabled: loading || busy || !workspace,
      onClick: () => {
        setWizardError('');
        setWizardMessage('');
        setWizardOpen(true);
      },
    },
    ...(DATABASE_PARTITION_ROLLBACK_ENABLED
      && workspace?.CanRequestRollback ? [{
        key: 'request-database-partition-rollback',
        label: 'Request Rollback',
        iconKey: 'undo',
        disabled: !workspace?.CanRequestRollback || busy,
        onClick: () => beginAction(
          CLIENT_CONFIGURATION_OPERATIONS.requestPartitionRollback,
          'Start returning to the shared database',
          'Keep using the client database while administrators copy its latest data back to the shared PlatformApp database.',
        ),
      }] : []),
    ...(DATABASE_PARTITION_ROLLBACK_ENABLED
      && workspace?.CanCompleteRollback ? [{
        key: 'complete-database-partition-rollback',
        label: 'Complete Rollback',
        iconKey: 'undo',
        disabled: !workspace?.CanCompleteRollback || busy,
        danger: true,
        onClick: () => beginAction(
          CLIENT_CONFIGURATION_OPERATIONS.completePartitionRollback,
          'Return to the shared database',
          'After all current data has been copied back, switch this client to the shared PlatformApp database.',
          true,
        ),
      }] : []),
  ];

  const runWizardStep = (stepKey) => {
    if (stepKey === 'identity') {
      beginAction(
        CLIENT_CONFIGURATION_OPERATIONS.setUpPartitionIdentity,
        'Save the client database name',
        workspace?.ProposedPartitionKey
          ? `Save ${workspace.ProposedDatabaseName} as this client's permanent database name. The application will continue using the shared PlatformApp database.`
          : 'Save this client’s permanent database name. The application will continue using the shared PlatformApp database.',
      );
      return;
    }
    if (stepKey === 'prepare') {
      beginAction(
        CLIENT_CONFIGURATION_OPERATIONS.preparePartition,
        'Start client database setup',
        'Start setup for the selected client database. The application will continue using the shared PlatformApp database until activation.',
      );
      return;
    }
    if (stepKey === 'validate') {
      beginAction(
        CLIENT_CONFIGURATION_OPERATIONS.validatePartition,
        'Check the client database',
        'Confirm that Convoke can connect to the selected client database and that it is ready to use. Client traffic will not move during this check.',
      );
      return;
    }
    if (stepKey === 'deploy') {
      beginAction(
        CLIENT_CONFIGURATION_OPERATIONS.deployPartition,
        'Deploy the client database',
        workspace?.MigrationStrategy === 'NewClientInitialization'
          ? 'Create the database and reconcile every supported client-only collection and index. The application will continue using the shared database until activation.'
          : 'Create the database, reconcile every supported client-only collection and index, and copy this client’s existing data. The application will continue using the shared database until activation.',
        false,
        true,
      );
      return;
    }
    if (stepKey === 'activate') {
      beginAction(
        CLIENT_CONFIGURATION_OPERATIONS.activatePartition,
        'Start using the client database',
        'Switch this client’s PlatformApp data to the prepared database. New client-specific writes will use it after confirmation.',
      );
    }
  };

  return (
    <div className="resource-workspace-overview command-entry command-configuration">
      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Database Partition actions"
        className="convoke-command-bar--screen"
        useSparkButtons
      />
      <ConfigurationState loading={loading} error={error} message={message} />
      {!loading && workspace ? (
        <>
          {identityUnavailable ? (
            <div className="resource-workspace-access-note" role="status">
              <strong>{workspace.CapabilityStatus}</strong>
              {workspace.CapabilityMessage
                ? ` — ${workspace.CapabilityMessage}`
                : null}
            </div>
          ) : null}
          {!identityUnavailable ? (
            <PartitionPanels workspace={workspace} />
          ) : null}
        </>
      ) : null}

      <AIPanel
        open={wizardOpen}
        presentation="slideout"
        title="Manage Database Partition"
        className="ai-panel-floating-slideout"
        mapToClass="core-layout"
        width={620}
        minPanelWidth={620}
        panelStorageKey={null}
        chatEnabled={false}
        chatPaneToggleEnabled={false}
        singleSessionMode
        onCancel={() => setWizardOpen(false)}
      >
        <ClientDatabasePartitionWizard
          open={wizardOpen}
          workspace={wizardWorkspace}
          metadata={metadata}
          formFields={formFields}
          canSaveConfigurationPermission={partitionPermissions.configuration}
          canRunStep={(stepKey) => partitionPermissions[stepKey] === true}
          busy={busy}
          error={wizardError}
          message={wizardMessage}
          onRefresh={() => {
            setWizardError('');
            setWizardMessage('');
            return load(
              CLIENT_CONFIGURATION_OPERATIONS.loadPartitionStatus,
            );
          }}
          onSaveConfiguration={saveRequest}
          onRunStep={runWizardStep}
          eventManagerHref={
            workspace?.DeploymentEventId
              ? getEventManagerHref(workspace.DeploymentEventId)
              : ''
          }
        />
      </AIPanel>

      <DialogControl
        openDialog={inventoryOpen}
        title="Client database migration check"
        subTitle="Remaining active client-required records in the shared PlatformApp database."
        okText="Close"
        cancelText=""
        onCancel={() => setInventoryOpen(false)}
        onX={() => setInventoryOpen(false)}
        onOk={() => setInventoryOpen(false)}
      >
        <div className="command-partition-deployment-inventory">
          <article className="command-partition-deployment-inventory__total">
            <div>
              <strong>Active shared records</strong>
              <span>
                {deploymentSourceRecordCount > 0
                  ? 'These records must be reconciled, verified, and soft-deleted from normal shared-data access.'
                  : 'No active client-required records remain in shared storage.'}
              </span>
            </div>
            <strong>
              {deploymentSourceRecordCount.toLocaleString()}
              {' record(s)'}
            </strong>
          </article>
          {deploymentInventory.length ? (
            deploymentInventory.map((entry) => (
              <article
                className="resource-workspace-record-row"
                key={entry.CollectionName}
              >
                <div>
                  <strong>{entry.CollectionName}</strong>
                  <span>
                    {(entry.ItemTypes || []).length}
                    {' supported item type(s)'}
                  </span>
                </div>
                <strong className="resource-workspace-record-row-status">
                  {Number(entry.SourceRecordCount || 0).toLocaleString()}
                  {' record(s)'}
                </strong>
              </article>
            ))
          ) : (
            <p>
              No active client-required records remain in the shared
              PlatformApp database.
            </p>
          )}
        </div>
      </DialogControl>

      <SidePanelFormControl
        open={connectionOpen}
        title="Update Dedicated Connection"
        metadata={metadata}
        formFields={connectionFormFields}
        itemData={connectionDraft}
        onSave={updateDedicatedConnection}
        onCancel={() => setConnectionOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText={busy ? 'Validating...' : 'Validate and Update'}
        setOpenEditSidePanel={setConnectionOpen}
        setItemData={setConnectionDraft}
        viewOnly={false}
      />

      <DialogControl
        openDialog={!!pendingAction}
        title={pendingAction?.title}
        subTitle={pendingAction?.description}
        okText={
          pendingAction?.requiresReconcile && pendingAction?.step === 1
            ? 'Continue'
            : 'Confirm'
        }
        cancelText="Cancel"
        onCancel={() => setPendingAction(null)}
        onX={() => setPendingAction(null)}
        onOk={runLifecycleAction}
        disableOk={busy}
        loadingOk={busy}
      >
        {pendingAction?.requiresReconcile && pendingAction?.step === 2 ? (
          <p>
            Confirm that all current client data has been copied back. This
            will switch the client to the shared PlatformApp database.
          </p>
        ) : (
          <div className="command-partition-confirmation">
            {showsDatabaseDestination ? (
              <dl className="command-partition-confirmation__summary">
                <div>
                  <dt>Database</dt>
                  <dd>{workspace?.ExpectedDatabaseName || 'Not configured'}</dd>
                </div>
                <div>
                  <dt>MongoDB server</dt>
                  <dd>{workspace?.TargetDatabaseServer || 'Not configured'}</dd>
                </div>
                <div>
                  <dt>Storage</dt>
                  <dd>
                    {formatPartitionValue(workspace?.StorageConfiguration)}
                  </dd>
                </div>
                {pendingAction?.operationName
                  === CLIENT_CONFIGURATION_OPERATIONS.deployPartition ? (
                    <>
                      <div>
                        <dt>Collections</dt>
                        <dd>
                          {deploymentInventory.length}
                          {' supported client-only collection(s)'}
                        </dd>
                      </div>
                      <div>
                        <dt>Source records</dt>
                        <dd>
                          {deploymentSourceRecordCount.toLocaleString()}
                        </dd>
                      </div>
                    </>
                  ) : null}
              </dl>
            ) : null}
            <p className="command-partition-confirmation__prompt">
              Select Confirm to continue with this step.
            </p>
          </div>
        )}
      </DialogControl>
    </div>
  );
};

const escapeHtml = (value) => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const previewDocument = (draft, mode) => {
  const theme = draft?.[mode] || {};
  const dark = mode === 'Dark';
  const css = themeModeCss(theme);
  return `<!doctype html>
<html><head><meta charset="utf-8"><style>
body{font-family:Arial,sans-serif;margin:0;background:${dark ? '#151719' : '#f5f7f9'};color:${dark ? '#f2f4f6' : '#202428'}}
.shell{display:grid;grid-template-columns:12rem 1fr;min-height:100vh}.nav{background:${dark ? '#20252a' : '#e6ebef'};padding:1rem}.main{padding:1.5rem}
.card{background:${dark ? '#252b30' : '#fff'};border:1px solid ${dark ? '#3c444b' : '#cad2d9'};border-radius:.5rem;margin-top:1rem;padding:1rem}
button{border:0;border-radius:.35rem;background:#3178c6;color:white;padding:.55rem .85rem}
${css}
</style><title>${escapeHtml(draft?.DocumentTitle || draft?.Name || 'Theme Preview')}</title></head>
<body><div class="shell"><aside class="nav"><strong>Convoke</strong><p>Command</p><p>Clients</p><p>Activity</p></aside>
<main class="main"><h1>${escapeHtml(draft?.Name || 'Theme Preview')}</h1><p>${escapeHtml(draft?.Description || 'Isolated client theme preview')}</p>
<section class="card"><h2>Client workspace</h2><p>Sample navigation, content, form, and status surfaces.</p><button type="button">Primary Action</button></section>
<section class="card"><strong>Ready</strong><p>This preview does not restyle the active application shell.</p></section></main></div></body></html>`;
};

const ThemeAssets = ({ draft }) => {
  const groups = ['Light', 'Dark'].map((mode) => {
    const theme = draft?.[mode] || {};
    return {
      mode,
      rows: [
        ['Header Image', theme.HeaderImage],
        ['Icon File', theme.IconFileId],
        ['Favicon File', theme.FaviconFileId],
        ['Loading SVG', theme.LoadingSVG ? 'Configured' : 'Not configured'],
      ],
    };
  });
  return (
    <section className="command-theme__assets" aria-label="Theme assets">
      {groups.map(({ mode, rows }) => (
        <section className="command-theme__asset-group" key={mode}>
          <h3>{mode} Mode</h3>
          <dl className="resource-workspace-facts">
            {rows.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value || 'Not configured'}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </section>
  );
};

export const ClientThemeScreen = ({
  screen,
  clientId,
  appId,
  currentEnvironment,
}) => {
  const {
    canCreate,
    canExecute,
  } = useCorePermissions();
  const workspaceMgr = useGenericManager(
    CLIENT_CONFIGURATION_ITEM_NAMES.themeWorkspace,
  );
  const pairMgr = useGenericManager(
    CLIENT_CONFIGURATION_ITEM_NAMES.themePair,
  );
  const themeMgr = useGenericManager(
    CLIENT_CONFIGURATION_ITEM_NAMES.theme,
  );
  const workspaceMgrRef = useRef(workspaceMgr);
  const pairMgrRef = useRef(pairMgr);
  const themeMgrRef = useRef(themeMgr);
  const [workspace, setWorkspace] = useState(null);
  const [pairMetadata, setPairMetadata] = useState(null);
  const [themeMetadata, setThemeMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [draft, setDraft] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [designerOpen, setDesignerOpen] = useState(false);
  const [activePreviewTab, setActivePreviewTab] = useState('light');
  const [modePanel, setModePanel] = useState(null);
  const [modeDraft, setModeDraft] = useState(null);
  const [addThemeOpen, setAddThemeOpen] = useState(false);
  const [addThemeDraft, setAddThemeDraft] = useState(null);
  const [pendingActivation, setPendingActivation] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  workspaceMgrRef.current = workspaceMgr;
  pairMgrRef.current = pairMgr;
  themeMgrRef.current = themeMgr;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const [loaded, pairMetadataResult, themeMetadataResult] =
        await Promise.all([
          runOperation(
            workspaceMgrRef.current,
            CLIENT_CONFIGURATION_OPERATIONS.loadThemePairs,
            {
              ClientId: clientId,
              ApplicationId: appId,
              CurrentEnvironment: currentEnvironment,
            },
            'Client Theme pairs are unavailable.',
          ),
          pairMgrRef.current.GetMetadata({ forceReload: true }),
          themeMgrRef.current.GetMetadata({ forceReload: true }),
        ]);
      setWorkspace(loaded);
      setPairMetadata(
        getFirstMetadata(pairMetadataResult, pairMgrRef.current),
      );
      setThemeMetadata(
        getFirstMetadata(themeMetadataResult, themeMgrRef.current),
      );
    } catch (ex) {
      setWorkspace(null);
      setError(ex?.message || 'Client Theme pairs are unavailable.');
    } finally {
      setLoading(false);
    }
  }, [appId, clientId, currentEnvironment]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const themeFormFields = useMemo(
    () => selectFields(themeMetadata, THEME_MODE_FORM_FIELDS),
    [themeMetadata],
  );
  const canCreateTheme = canCreate(
    CLIENT_COMMAND_PRIMARY_ITEM_PERMISSIONS.theme,
  );
  const canSaveThemePair = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientThemePairDraft,
  );
  const canPreviewThemePair = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.previewThemeDraftPair,
  );
  const canActivateThemePair = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.activateThemePair,
  );
  const canDeleteThemePair = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.deleteClientThemePair,
  );
  const canUseThemeAI = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.aiQuestionThemeManage,
  );
  const previewStorageKey =
    `client-theme-preview:${clientId}:${appId}`;

  useEffect(() => {
    try {
      const storedIndex = Number(
        window.localStorage.getItem(previewStorageKey),
      );
      setActivePreviewTab(
        ['light', 'dark', 'assets'][storedIndex] || 'light',
      );
    } catch {
      setActivePreviewTab('light');
    }
  }, [previewStorageKey]);

  const openDesigner = (pair = null) => {
    const next = pair
      ? createThemeDraftFromPair(pair, clientId)
      : createBlankThemePairDraft(clientId);
    setWorkspace((current) => ({
      ...current,
      SelectedMasterThemeId: next.MasterThemeId,
      LightConcurrencyToken:
        pair && !pair.IsActivePair ? pair.LightConcurrencyToken : '',
      DarkConcurrencyToken:
        pair && !pair.IsActivePair ? pair.DarkConcurrencyToken : '',
    }));
    setDraft(next);
    setBaseline(JSON.parse(JSON.stringify(next)));
    setDesignerOpen(true);
  };

  const openModePanel = (mode) => {
    const currentMode = draft?.[mode] ?? {};
    const defaultName = mode === 'Dark' ? 'Dark Mode' : 'Light Mode';
    setModePanel(mode);
    setModeDraft({
      ...currentMode,
      Name: String(currentMode.Name ?? '').trim() || defaultName,
    });
  };

  const openAddThemePanel = () => {
    setAddThemeDraft({
      IsNew: true,
      ClientId: clientId,
      ProfileId: null,
      IsDefault: false,
      IsActive: false,
    });
    setAddThemeOpen(true);
  };

  const saveTheme = async (updated) => {
    const payload = {
      ...updated,
      IsNew: true,
      ClientId: clientId,
      ProfileId: null,
    };
    const result = await themeMgrRef.current.saveItem(payload);
    if (!isSuccessfulResult(result)) return result;

    setAddThemeOpen(false);
    setAddThemeDraft(null);
    await refresh();
    setMessage('The Theme was added.');
    return result;
  };

  const updateMode = async (updated) => {
    const next = {
      ...draft,
      [modePanel]: {
        ...updated,
        MasterThemeId: draft.MasterThemeId,
        ClientId: clientId,
        ProfileId: null,
        IsDefault: false,
        IsActive: false,
      },
    };
    setDraft(next);
    setModePanel(null);
    setModeDraft(null);
    return { Success: true, Items: [next] };
  };

  const executeDraftOperation = async (operationName, successMessage) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await runOperation(
        workspaceMgrRef.current,
        operationName,
        {
          ...workspace,
          ClientId: clientId,
          ApplicationId: appId,
          CurrentEnvironment: currentEnvironment,
          SelectedMasterThemeId: draft.MasterThemeId,
          Draft: draft,
        },
        successMessage,
      );
      setWorkspace(saved);
      if (saved.Draft) {
        setDraft(saved.Draft);
        if (operationName ===
            CLIENT_CONFIGURATION_OPERATIONS.saveThemePairDraft) {
          setBaseline(JSON.parse(JSON.stringify(saved.Draft)));
        }
      }
      setMessage(successMessage);
      return saved;
    } catch (ex) {
      setError(ex?.message || successMessage);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const activatePair = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await runOperation(
        workspaceMgrRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.activateThemePair,
        {
          ...workspace,
          ClientId: clientId,
          ApplicationId: appId,
          CurrentEnvironment: currentEnvironment,
          SelectedMasterThemeId: pendingActivation.MasterThemeId,
          ConfirmActivation: true,
        },
        'The Theme pair was not activated.',
      );
      setWorkspace(saved);
      setPendingActivation(null);
      setDesignerOpen(false);
      setMessage('The complete Light and Dark Theme pair is active.');
      return { Success: true, Items: [saved] };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage: ex?.message || 'The Theme pair was not activated.',
      };
    } finally {
      setBusy(false);
    }
  };

  const deletePair = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await runOperation(
        workspaceMgrRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.deleteThemePair,
        {
          ...workspace,
          ClientId: clientId,
          ApplicationId: appId,
          CurrentEnvironment: currentEnvironment,
          SelectedMasterThemeId: pendingDelete.MasterThemeId,
          ConfirmDelete: true,
        },
        'The Theme pair was not deleted.',
      );
      setWorkspace(saved);
      setPendingDelete(null);
      setMessage('Both inactive Theme siblings were deleted.');
      return { Success: true, Items: [saved] };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage: ex?.message || 'The Theme pair was not deleted.',
      };
    } finally {
      setBusy(false);
    }
  };

  const rowActions = () => ([
    ...(canSaveThemePair
      ? [{ name: 'Design / Edit', customFunction: openDesigner }]
      : []),
    ...(canActivateThemePair
      ? [{ name: 'Activate Pair', customFunction: setPendingActivation }]
      : []),
    ...(canDeleteThemePair
      ? [{ name: 'Delete Pair', customFunction: setPendingDelete }]
      : []),
  ]);

  const savedDraftPair = workspace?.Pairs?.find(
    (pair) => String(pair.MasterThemeId) === String(draft?.MasterThemeId),
  );
  const designer = draft ? (
    <section
      className="command-theme__designer"
      aria-label="Theme pair designer"
    >
      <div className="resource-workspace-section-title resource-workspace-section-title--embedded command-theme__designer-header">
        <div>
          <h2>{draft.Name || 'New Theme Pair'}</h2>
          <p>
            Forced client scope · paired Light and Dark modes · inactive draft
          </p>
        </div>
      </div>
      <div
        className="command-entry__actions command-theme__actions"
        role="toolbar"
        aria-label="Theme draft actions"
      >
        {canSaveThemePair && activePreviewTab !== 'assets' ? (
          <ButtonControl
            type="icon-small"
            onClick={() => openModePanel(
              activePreviewTab === 'dark' ? 'Dark' : 'Light',
            )}
            disabled={busy}
            aria-label={
              activePreviewTab === 'dark' ? 'Edit Dark' : 'Edit Light'
            }
          >
            <IconControl
              iconKey="edit"
              title={
                activePreviewTab === 'dark' ? 'Edit Dark' : 'Edit Light'
              }
              width="1.125rem"
              height="1.125rem"
            />
          </ButtonControl>
        ) : null}
        {canPreviewThemePair ? <ButtonControl
          type="icon-small"
          onClick={() => executeDraftOperation(
            CLIENT_CONFIGURATION_OPERATIONS.previewThemeDraftPair,
            'The isolated preview was refreshed.',
          )}
          disabled={busy}
          aria-label="Refresh Preview"
        >
          <IconControl
            iconKey="refresh"
            title="Refresh Preview"
            width="1.125rem"
            height="1.125rem"
          />
        </ButtonControl> : null}
        {canSaveThemePair ? <ButtonControl
          type="icon-small"
          onClick={() => executeDraftOperation(
            CLIENT_CONFIGURATION_OPERATIONS.saveThemePairDraft,
            'The complete Theme pair draft was saved.',
          )}
          disabled={busy}
          aria-label="Save Draft"
        >
          <IconControl
            iconKey="save"
            title="Save Draft"
            width="1.125rem"
            height="1.125rem"
          />
        </ButtonControl> : null}
        <ButtonControl
          type="icon-small"
          onClick={() => setDraft(
            JSON.parse(JSON.stringify(baseline)),
          )}
          disabled={busy || !baseline}
          aria-label="Roll Back Draft"
        >
          <IconControl
            iconKey="undo"
            title="Roll Back Draft"
            width="1.125rem"
            height="1.125rem"
          />
        </ButtonControl>
        {canActivateThemePair ? <ButtonControl
          type="icon-small"
          onClick={() => setPendingActivation(savedDraftPair)}
          disabled={busy || !savedDraftPair || savedDraftPair.IsActivePair}
          aria-label="Activate Saved Pair"
        >
          <IconControl
            iconKey="success"
            title="Activate Saved Pair"
            width="1.125rem"
            height="1.125rem"
          />
        </ButtonControl> : null}
      </div>
      <ConvokeDialogMessage
        message={workspace?.PreviewValidationErrors?.join(' ')}
        tone="error"
      />
      <TabPaneControl
        activeKey={activePreviewTab}
        align="right"
        className="command-theme__tabs"
        framed={false}
        onChange={(_index, tab) => {
          setActivePreviewTab(tab?.key || 'light');
        }}
        storageKey={previewStorageKey}
        unmountInactive
        tabs={[
          {
            key: 'light',
            title: 'Light Preview',
            content: (
              <iframe
                className="command-theme__preview"
                title="Light Theme isolated preview"
                sandbox=""
                srcDoc={previewDocument(draft, 'Light')}
              />
            ),
          },
          {
            key: 'dark',
            title: 'Dark Preview',
            content: (
              <iframe
                className="command-theme__preview"
                title="Dark Theme isolated preview"
                sandbox=""
                srcDoc={previewDocument(draft, 'Dark')}
              />
            ),
          },
          {
            key: 'assets',
            title: 'Assets',
            content: <ThemeAssets draft={draft} />,
          },
        ]}
      />
    </section>
  ) : null;
  const commandBarActions = [
    {
      key: 'refresh-client-themes',
      label: 'Refresh',
      iconKey: 'refresh',
      disabled: loading || busy,
      onClick: refresh,
    },
    ...(canCreateTheme ? [{
        key: 'add-client-theme',
        label: 'Add Theme',
        iconKey: 'add',
        disabled: !themeMetadata || busy,
        onClick: openAddThemePanel,
      }] : []),
    ...(canSaveThemePair ? [{
        key: 'add-client-theme-pair',
        label: 'New Theme Pair',
        iconKey: 'add',
        disabled: !workspace || !themeMetadata || busy,
        onClick: () => openDesigner(),
      }] : []),
  ];

  return (
    <div className="resource-workspace-overview command-entry command-configuration">
      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Theme actions"
        className="convoke-command-bar--screen"
        useSparkButtons
      />
      <ConfigurationState loading={loading} error={error} message={message} />
      {!loading && workspace && pairMetadata ? (
        <>
          <ClientThemeConfigurationSummary workspace={workspace} />
          {workspace.Pairs?.length > 0 ? (
            <TableControl
              data={workspace.Pairs}
              defaultNumRows={25}
              disableTableTitle
              enableMultiSelect={false}
              enableSidePanelEdit={false}
              filters={pairMgr?.filters}
              loadingState={loading || busy}
              metadata={[pairMetadata]}
              mgr={pairMgr}
              onEdit={canSaveThemePair ? openDesigner : undefined}
              customQuickViewList={rowActions}
            />
          ) : null}
        </>
      ) : null}

      <AIPanel
        open={designerOpen}
        presentation="slideout"
        mapToClass="core-layout"
        title="AI Theme Designer"
        onCancel={() => setDesignerOpen(false)}
        onUpdatePanel={(uiAction, item, value) => {
          setDraft((current) => resolveThemeAIUpdate(
            item,
            value,
            current,
            clientId,
          ));
        }}
        operationName={
          canUseThemeAI
            ? CLIENT_CONFIGURATION_OPERATIONS.aiQuestionThemeManage
            : undefined
        }
        chatEnabled={canUseThemeAI}
        referenceId={draft?.MasterThemeId}
        referenceName="ThemeManage"
        params={{
          clientId,
          applicationId: appId,
          currentEnvironment,
          masterThemeId: draft?.MasterThemeId,
        }}
        payload={draft}
        chatPane="right"
        chatPaneCollapseMode="open"
        chatPaneStartOpen
        aiProcessingMode="Direct"
        singleSessionMode
        planningPreviewEnabled
        previewPanelEnabled={false}
        splashMessage="Ask AI to refine this paired client Theme draft."
        introMessage="Describe the Light and Dark appearance you want. AI can update only this in-memory draft; Save Draft and Activate remain explicit administrator actions."
        minPanelWidth={840}
        minLeft={480}
        minRight={380}
      >
        {designer}
      </AIPanel>

      <SidePanelFormControl
        open={addThemeOpen}
        title="Add Theme"
        metadata={themeMetadata}
        formFields={themeMetadata?.Fields ?? themeMetadata?.fields ?? []}
        itemData={addThemeDraft}
        onSave={saveTheme}
        onCancel={() => setAddThemeOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText={busy ? 'Adding...' : 'Add'}
        setOpenEditSidePanel={setAddThemeOpen}
        setItemData={setAddThemeDraft}
        viewOnly={false}
      />

      <SidePanelFormControl
        open={!!modePanel}
        title={`Edit ${modePanel || ''} Theme`}
        metadata={themeMetadata}
        formFields={themeFormFields}
        itemData={modeDraft}
        onSave={updateMode}
        onCancel={() => setModePanel(null)}
        onUpdateSidePanel={() => {}}
        saveButtonText="Apply to Draft"
        setOpenEditSidePanel={(open) => {
          if (!open) setModePanel(null);
        }}
        setItemData={setModeDraft}
        viewOnly={false}
      />

      <DialogControl
        openDialog={!!pendingActivation}
        title="Activate Theme pair"
        subTitle="Activation changes both Light and Dark siblings together for the selected client and application."
        okText="Confirm Activation"
        cancelText="Cancel"
        onCancel={() => setPendingActivation(null)}
        onX={() => setPendingActivation(null)}
        onOk={activatePair}
        disableOk={busy || !pendingActivation?.IsComplete}
        loadingOk={busy}
      >
        <p>
          Activate {pendingActivation?.Name || 'the selected complete pair'}?
          The previously active pair will be deactivated.
        </p>
      </DialogControl>

      <DialogControl
        openDialog={!!pendingDelete}
        title="Delete inactive Theme pair"
        subTitle="Both Light and Dark siblings are deleted together. The active pair cannot be deleted."
        okText="Confirm Delete"
        cancelText="Cancel"
        onCancel={() => setPendingDelete(null)}
        onX={() => setPendingDelete(null)}
        onOk={deletePair}
        disableOk={busy || pendingDelete?.IsActivePair}
        loadingOk={busy}
      >
        <p>
          Delete both siblings for {pendingDelete?.Name || 'the selected pair'}?
        </p>
      </DialogControl>
    </div>
  );
};
